import { useState, useEffect, useCallback } from 'react';
import {
  DialogContent, DialogActions, Button, Alert, Box, CircularProgress,
  Typography, TextField, Autocomplete, Tabs, Tab, Chip,
  Paper,
} from '@mui/material';
import TooltipIconButton from '../../components/TooltipIconButton';
import { Add, Delete, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useModalState } from '../../hooks/useModalState';
import { useDirtyFormGuard } from '../../hooks/useDirtyFormGuard';
import { FormikTextField, FormikTagSelect } from '../../components/FormikFields';
import FormDirtyTracker from '../../components/FormDirtyTracker';
import DraggableDialog from '../../components/DraggableDialog';
import { CopyId } from '../../components/CopyId';
import { HistoryButton } from '../../components/EntityHistory';
import { flowsService, RuleCondition, FlowRuleGroup, FlowPath } from '../../services/flows.service';
import { EntitySelectFieldFormik } from '../../components/EntitySelectField';


// ---------- Local form types ----------

/** A group as this flow uses it: identity + ordering + weight, not editable contents. */
interface AttachedGroup {
  id: string;
  name: string;
  conditions: RuleCondition[];
  weight: number;
  used_by_flow_count: number;
  paths: FlowPath[];
}

interface FlowFormValues {
  name: string;
  notes: string;
  ref: string;
  traffic_cap_limit: number;
  daily_conversion_cap: number;
  traffic_source_id: string;
  ad_account_id: string | null;
  tag_ids: string[];
}

const schema = Yup.object({
  traffic_source_id: Yup.string().required('Traffic source is required'),
  name: Yup.string().trim().required('Name is required'),
  traffic_cap_limit: Yup.number().min(0).default(0),
  daily_conversion_cap: Yup.number().min(0).default(0),
});

export default function CreateEditFlow() {
  const { modalState, toggleModal } = useModalState('flow');
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const close = useCallback(() => toggleModal(false), [toggleModal]);
  const { guardedClose, setDirty, setSubmitted } = useDirtyFormGuard(close);

  const { data: entity, isLoading, refetch } = useQuery({
    queryKey: ['flow', modalState.id],
    queryFn: () => flowsService.getById(modalState.id!),
    enabled: !!modalState.id && modalState.open,
  });

  useEffect(() => { if (modalState.id && modalState.open) refetch(); }, [modalState.id, modalState.open, refetch]);


  const isEdit = modalState.reason === 'Update' && !!entity;
  const isDuplicate = modalState.reason === 'Duplicate';
  const title = isEdit ? `Edit: ${entity?.name}` : isDuplicate ? 'Duplicate Flow' : 'Create Flow';

  const [dialogTab, setDialogTab] = useState(0);

  const initialValues: FlowFormValues = {
    name: entity ? (isDuplicate ? `${entity.name} (copy)` : entity.name) : '',
    notes: entity?.notes ?? '',
    ref: entity && !isDuplicate ? (entity.ref ?? '') : '',
    traffic_cap_limit: entity?.traffic_cap_limit ?? 0,
    daily_conversion_cap: entity?.daily_conversion_cap ?? 0,
    traffic_source_id: entity?.traffic_source_id ?? (modalState.defaults?.traffic_source_id as string) ?? '',
    // Not copied on Duplicate: the wrong account silently mis-attributes spend.
    ad_account_id: (isDuplicate ? null : entity?.ad_account_id) || null,
    tag_ids: entity?.tag_ids ?? [],
  };

  /**
   * Groups this flow uses, in evaluation order. A flow only chooses WHICH groups and
   * their order — the contents live in the rule group editor, so saving a flow can
   * never rewrite routing that other flows share.
   */
  const [attachedGroups, setAttachedGroups] = useState<AttachedGroup[]>([]);
  const { setModal: setGroupModal } = useModalState('rulegroup');
  const [groupSearch, setGroupSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounced so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(groupSearch), 250);
    return () => clearTimeout(t);
  }, [groupSearch]);

  // Server-side search: the workspace's group library is unbounded, so the picker
  // must never try to hold all of it.
  const { data: groupSearchResult, isFetching: searchingGroups } = useQuery({
    queryKey: ['rule-groups-search', debouncedSearch],
    queryFn: () => flowsService.listRuleGroups({ search: debouncedSearch, limit: 50 }),
    enabled: modalState.open,
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    if (!modalState.open) return;
    if (entity) {
      const groups = (entity.rule_groups || []).map(g => ({
        id: g.id,
        name: g.name,
        conditions: g.conditions || [],
        weight: g.weight ?? 100,
        used_by_flow_count: g.used_by_flow_count ?? 1,
        paths: g.paths || [],
      }));
      // A duplicate attaches the SAME groups. Sharing is the feature: one funnel,
      // many flows, edited in one place.
      setAttachedGroups(groups);
    } else {
      setAttachedGroups([]);
    }
  }, [entity, isDuplicate, modalState.open]);

  // Pick up groups created from this dialog's "New Group" button.
  useEffect(() => {
    if (!modalState.open) return;
    const onCreated = (e: Event) => {
      const g = (e as CustomEvent<FlowRuleGroup>).detail;
      if (!g?.id) return;
      setAttachedGroups(prev => prev.some(x => x.id === g.id) ? prev : [...prev, {
        id: g.id,
        name: g.name,
        conditions: g.conditions || [],
        weight: 100,
        used_by_flow_count: (g.used_by_flow_count ?? 0) + 1,
        paths: g.paths || [],
      }]);
    };
    window.addEventListener('rulegroup:created', onCreated);
    return () => window.removeEventListener('rulegroup:created', onCreated);
  }, [modalState.open]);

  const sharedGroups = attachedGroups.filter(g => (g.used_by_flow_count ?? 1) > 1);

  /** Routing weight-splits across the groups that match; order is just display order. */
  const moveGroup = (idx: number, dir: -1 | 1) => {
    setAttachedGroups(prev => {
      const t = idx + dir;
      if (idx < 0 || idx >= prev.length || t < 0 || t >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[t]] = [next[t], next[idx]];
      return next;
    });
  };

  const setGroupWeight = (id: string, weight: number) =>
    setAttachedGroups(prev => prev.map(g => g.id === id ? { ...g, weight } : g));

  const attachGroup = (g: FlowRuleGroup) => {
    if (attachedGroups.some(x => x.id === g.id)) {
      enqueueSnackbar('Already attached to this flow', { variant: 'info' });
      return;
    }
    setAttachedGroups(prev => [...prev, {
      id: g.id,
      name: g.name,
      conditions: g.conditions || [],
      weight: 100,
      used_by_flow_count: (g.used_by_flow_count ?? 0) + 1,
      paths: g.paths || [],
    }]);
  };

  const describe = (g: AttachedGroup) => {
    if (!g.paths.length) return 'No paths';
    return g.paths.map(p => {
      const dest = p.lp_name ? `LP: ${p.lp_name}` : 'Direct';
      return `${dest} → ${(p.offers || []).length} offer${(p.offers || []).length === 1 ? '' : 's'} (w:${p.weight})`;
    }).join('  |  ');
  };

  return (
    <DraggableDialog open={modalState.open} onClose={guardedClose} maxWidth="lg" fullWidth
      title={isLoading ? 'Loading...' : title} titleId="flow-dialog-title">
      {isLoading && modalState.id ? (
        <DialogContent><Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box></DialogContent>
      ) : (
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={schema}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            try {
              setSubmitted();
              // Position is the array order; weight rides along per group.
              const rule_groups = attachedGroups.map(g => ({ id: g.id, weight: g.weight }));
              const flowFields = {
                name: values.name, notes: values.notes, ref: values.ref, traffic_source_id: values.traffic_source_id,
                ad_account_id: values.ad_account_id, traffic_cap_limit: Number(values.traffic_cap_limit),
                daily_conversion_cap: Number(values.daily_conversion_cap), tag_ids: values.tag_ids,
              };

              if (!isEdit) {
                await flowsService.create({ ...flowFields, rule_groups });
              } else {
                await flowsService.update(entity!.id, { ...flowFields, rule_groups });
              }

              queryClient.invalidateQueries({ queryKey: ['flows'] });
              close();
              enqueueSnackbar('Saved', { variant: 'success' });
            } catch (err: any) {
              setStatus(err?.response?.data?.error?.message || err.message || 'Save failed');
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ submitForm, isSubmitting, status }: { submitForm: () => Promise<void>; isSubmitting: boolean; status?: string }) => (
            <>
              <FormDirtyTracker onDirtyChange={setDirty} />
              <DialogContent dividers>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CopyId id={entity?.id} show={isEdit} />
                  <Box sx={{ flex: 1 }} />
                  {isEdit && <HistoryButton entityType="flows" entityId={entity!.id} entityName={entity?.name} />}
                </Box>
                <Tabs variant="fullWidth" value={dialogTab} onChange={(_, v) => setDialogTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Tab label="General" />
                  <Tab label={`Rule Groups (${attachedGroups.length})`} />
                </Tabs>

                {dialogTab === 0 && (
                  <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                      <FormikTextField name="name" label="Flow Name" size="small" sx={{ flex: 1 }} />
                      <FormikTextField name="ref" label="Ref" size="small" placeholder="e.g. F1200" sx={{ width: { xs: '100%', sm: 160 } }} />
                    </Box>
                    <FormikTextField name="notes" label="Notes" fullWidth size="small" multiline rows={2} />
                    {!modalState.hideParent && (
                      <EntitySelectFieldFormik
                        name="traffic_source_id" field="traffic_source_id" label="Traffic Source" required
                      />
                    )}
                    <EntitySelectFieldFormik
                      name="ad_account_id" field="ad_account_id" label="Ad Account (optional)"
                      helperText="Fills ad_account in the campaign URL. Leave empty to keep the REPLACE_ME placeholder."
                    />
                    <FormikTextField
                      name="traffic_cap_limit"
                      label="Daily Cap"
                      fullWidth
                      size="small"
                      type="number"
                      helperText="0 = unlimited"
                    />
                    <FormikTextField
                      name="daily_conversion_cap"
                      label="Daily Conversion Cap"
                      fullWidth
                      size="small"
                      type="number"
                      helperText="Max conversions per day before new traffic is diverted to the global fallback offer (0 = unlimited)."
                    />
                    <FormikTagSelect name="tag_ids" />
                  </Box>
                )}

                {dialogTab === 1 && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
                      Traffic weight-splits across the groups whose conditions match a visit (a group with no
                      conditions matches everyone). When nothing matches, the workspace fallback group serves.
                      Edit a group's landers and offers in the group itself — changes there apply to every flow using it.
                    </Typography>

                    {isDuplicate && attachedGroups.length > 0 && (
                      <Alert severity="info" sx={{ mb: 1.5 }}>
                        The copy will <b>share</b> these groups with the original, not duplicate them —
                        so editing the routing later updates both. Attach a different group here if this
                        flow should route differently.
                      </Alert>
                    )}

                    {sharedGroups.length > 0 && (
                      <Alert severity="info" sx={{ mb: 1.5 }}>
                        {sharedGroups.length === 1
                          ? <>“{sharedGroups[0].name || 'Unnamed'}” is shared with <b>{sharedGroups[0].used_by_flow_count - 1}</b> other flow{sharedGroups[0].used_by_flow_count - 1 === 1 ? '' : 's'}.</>
                          : <><b>{sharedGroups.length}</b> of these groups are shared with other flows.</>}
                      </Alert>
                    )}

                    {attachedGroups.length === 0 && (
                      <Alert severity="warning" sx={{ mb: 1.5 }}>
                        A flow needs at least one rule group before it can be saved — attach an
                        existing group or create a new one below.
                      </Alert>
                    )}

                    {attachedGroups.map((g, i) => (
                      <Paper key={g.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <TooltipIconButton size="small" sx={{ p: 0 }}
                              disabled={i === 0} title="Move up"
                              onClick={() => moveGroup(i, -1)}>
                              <ArrowUpward sx={{ fontSize: 14 }} />
                            </TooltipIconButton>
                            <TooltipIconButton size="small" sx={{ p: 0 }}
                              disabled={i >= attachedGroups.length - 1} title="Move down"
                              onClick={() => moveGroup(i, 1)}>
                              <ArrowDownward sx={{ fontSize: 14 }} />
                            </TooltipIconButton>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>{g.name || 'Unnamed group'}</Typography>
                              {g.conditions.length === 0 && <Chip size="small" variant="outlined" label="catch-all" />}
                              {g.conditions.length > 0 && <Chip size="small" variant="outlined" label={`${g.conditions.length} cond`} />}
                              <Chip
                                size="small"
                                color={g.used_by_flow_count > 1 ? 'warning' : 'default'}
                                variant={g.used_by_flow_count > 1 ? 'filled' : 'outlined'}
                                label={`${g.used_by_flow_count} flow${g.used_by_flow_count === 1 ? '' : 's'}`}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                              {describe(g)}
                            </Typography>
                          </Box>
                          <TextField
                            size="small" type="number" label="Weight" value={g.weight}
                            onChange={(e) => setGroupWeight(g.id, Math.max(0, Math.floor(Number(e.target.value) || 0)))}
                            sx={{ width: 88 }} inputProps={{ min: 0 }}
                          />
                          {/* Editing opens the group's own modal — the flow never rewrites it. */}
                          <Button
                            size="small"
                            title="Edit this group — applies to every flow using it"
                            onClick={() => setGroupModal({ open: true, id: g.id, reason: 'Update' })}
                          >
                            Edit
                          </Button>
                          <TooltipIconButton size="small" color="error"
                            title="Detach from this flow"
                            onClick={() => setAttachedGroups(prev => prev.filter(x => x.id !== g.id))}>
                            <Delete sx={{ fontSize: 16 }} />
                          </TooltipIconButton>
                        </Box>
                      </Paper>
                    ))}

                    <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                      <Autocomplete
                        size="small"
                        sx={{ flex: 1 }}
                        options={(groupSearchResult?.data || []).filter(g => !attachedGroups.some(a => a.id === g.id))}
                        getOptionLabel={(g: FlowRuleGroup) => g.name || 'Unnamed group'}
                        value={null}
                        loading={searchingGroups}
                        // Controlled input: selecting an option must clear the text,
                        // otherwise the next search appends to the previous one.
                        inputValue={groupSearch}
                        onInputChange={(_, v, reason) => { if (reason !== 'reset') setGroupSearch(v); }}
                        blurOnSelect
                        clearOnBlur
                        onChange={(_, v) => { if (v) { attachGroup(v); setGroupSearch(''); } }}
                        // Server already filtered; filtering again would hide matches
                        // whose name differs from the raw input.
                        filterOptions={(x) => x}
                        noOptionsText={searchingGroups ? 'Searching…' : 'No matching groups'}
                        renderOption={(props, g) => {
                          const { key, ...rest } = props as { key?: string } & Record<string, unknown>;
                          return (
                            <Box component="li" key={key} {...rest} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ flex: 1 }} noWrap>{g.name || 'Unnamed group'}</Typography>
                              <Chip size="small" label={`${g.paths.length}p`} />
                              <Chip size="small"
                                color={(g.used_by_flow_count ?? 0) > 1 ? 'warning' : 'default'}
                                label={`${g.used_by_flow_count ?? 0} flow${(g.used_by_flow_count ?? 0) === 1 ? '' : 's'}`} />
                            </Box>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Attach existing group — type to search…" size="small"
                            slotProps={{ input: { ...params.InputProps, endAdornment: (
                              <>{searchingGroups ? <CircularProgress size={14} /> : null}{params.InputProps.endAdornment}</>
                            ) } }} />
                        )}
                      />
                      <Button variant="outlined" startIcon={<Add />} sx={{ whiteSpace: 'nowrap' }}
                        onClick={() => setGroupModal({ open: true, reason: 'Create' })}>
                        New Group
                      </Button>
                    </Box>
                    {(groupSearchResult?.total ?? 0) > (groupSearchResult?.data.length ?? 0) && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Showing {groupSearchResult!.data.length} of {groupSearchResult!.total} — refine your search to narrow it.
                      </Typography>
                    )}
                  </>
                )}

                {status && <Alert severity="error" sx={{ mt: 2 }}>{status}</Alert>}
              </DialogContent>
              <DialogActions>
                <Button onClick={guardedClose}>Close</Button>
                <Button variant="contained" onClick={submitForm}
                  disabled={isSubmitting || attachedGroups.length === 0}
                  title={attachedGroups.length === 0 ? 'Attach at least one rule group first (Rules & Paths tab)' : undefined}
                  startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}>
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </DialogActions>
            </>
          )}
        </Formik>
      )}

    </DraggableDialog>
  );
}
